import React, { useEffect, useState } from 'react';
import {
  Box,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Grid,
  List,
  ListItem,
  ListItemText,
  Chip,
  styled
} from '@mui/material';
import { MapContainer, TileLayer, Marker, Popup, useMap } from 'react-leaflet';
import L from 'leaflet';
import { useTranslation } from 'react-i18next';
import { useAppContext } from '../../appContext';
import { dimensions } from '../../styles/dimensions';
import { $get } from '../../utils/http';
import { toast } from 'react-toastify';
import { useNavigate } from 'react-router-dom';
import 'leaflet/dist/leaflet.css';
import markerIcon from 'leaflet/dist/images/marker-icon.png';
import markerIcon2x from 'leaflet/dist/images/marker-icon-2x.png';
import markerShadow from 'leaflet/dist/images/marker-shadow.png';

const { baseMD } = dimensions;

interface Vehicle {
  id: number;
  licensePlate: string;
  brand: string;
  model: string;
  year: number;
  status: string;
  location?: {
    lat: number;
    lng: number;
    speed: number;
  };
}

// Styled components
const PageWrapper = styled(Box)({
  padding: '24px',
  height: 'calc(100vh - 64px - 48px)', // Viewport height minus header (64px) and padding (48px)
  display: 'flex',
  gap: '24px',
  overflow: 'hidden' // Prevent scrolling
});

const SideMenu = styled(Paper)(({ theme }) => ({
  width: '300px',
  height: '100%',
  overflowY: 'auto',
  padding: theme.spacing(2),
  display: 'flex',
  flexDirection: 'column'
}));

const MapWrapper = styled(Paper)({
  flex: 1,
  height: '100%',
  overflow: 'hidden', // Prevent map from causing scrollbars
  '& .leaflet-container': {
    height: '100%',
    width: '100%',
    borderRadius: '4px'
  }
});

const VehicleList = styled(List)({
  flex: 1,
  overflowY: 'auto',
  marginTop: '8px'
});

const VehicleListItem = styled(ListItem)(({ theme }) => ({
  marginBottom: theme.spacing(1),
  border: `1px solid ${theme.palette.divider}`,
  borderRadius: theme.shape.borderRadius,
  '&:hover': {
    backgroundColor: theme.palette.action.hover,
    cursor: 'pointer'
  }
}));

const StatusChip = styled(Chip)(({ status }: { status: string }) => ({
  marginLeft: 'auto',
  backgroundColor: status === 'Active' ? '#4caf50' : '#ff9800',
  color: 'white'
}));

// Nieuwe component voor map control
const MapController: React.FC<{ center?: [number, number] }> = ({ center }) => {
  const map = useMap();

  useEffect(() => {
    if (center) {
      map.setView(center, 14);
    }
  }, [center, map]);

  return null;
};

// Fix voor de marker iconen
delete (L.Icon.Default.prototype as any)._getIconUrl;
L.Icon.Default.mergeOptions({
  iconUrl: markerIcon,
  iconRetinaUrl: markerIcon2x,
  shadowUrl: markerShadow,
  iconSize: [25, 41],
  iconAnchor: [12, 41],
  popupAnchor: [1, -34],
  shadowSize: [41, 41]
});

// Definieer de auto iconen
const createCarIcon = (status: string) => {
  const color = status === 'Active' ? '#4caf50' : '#ff9800';
  return L.divIcon({
    className: 'custom-car-icon',
    html: `<svg 
      xmlns="http://www.w3.org/2000/svg" 
      viewBox="0 0 512 512" 
      style="width: 24px; height: 24px; fill: ${color};">
      <path d="M135.2 117.4L109.1 192H402.9l-26.1-74.6C372.3 104.6 360.2 96 346.6 96H165.4c-13.6 0-25.7 8.6-30.2 21.4zM39.6 196.8L74.8 96.3C88.3 57.8 124.6 32 165.4 32H346.6c40.8 0 77.1 25.8 90.6 64.3l35.2 100.5c23.2 9.6 39.6 32.5 39.6 59.2V400v48c0 17.7-14.3 32-32 32H448c-17.7 0-32-14.3-32-32V400H96v48c0 17.7-14.3 32-32 32H32c-17.7 0-32-14.3-32-32V400 256c0-26.7 16.4-49.6 39.6-59.2zM128 288a32 32 0 1 0 -64 0 32 32 0 1 0 64 0zm288 32a32 32 0 1 0 0-64 32 32 0 1 0 0 64z"/>
    </svg>`,
    iconSize: [24, 24],
    iconAnchor: [12, 12],
    popupAnchor: [0, -12]
  });
};

// Voeg CSS toe voor de custom icon
const IconStyle = styled('style')({
  children: `
    .custom-car-icon {
      background: none;
      border: none;
    }
    .custom-car-icon svg {
      filter: drop-shadow(2px 2px 2px rgba(0,0,0,0.5));
      transition: transform 0.3s ease;
    }
    .custom-car-icon:hover svg {
      transform: scale(1.2);
    }
  `
});

const FleetManagement: React.FunctionComponent = () => {
  const { t } = useTranslation();
  const { loadAdminProfile } = useAppContext();
  const [vehicles, setVehicles] = useState<Vehicle[]>([]);
  const navigate = useNavigate();
  const [selectedVehicle, setSelectedVehicle] = useState<[number, number] | null>(null);

  useEffect(() => {
    loadAdminProfile();
    fetchVehicles();
    // Start real-time updates
    const interval = setInterval(fetchVehicleLocations, 10000); // Update every 10 seconds
    return () => clearInterval(interval);
  }, []);

  const fetchVehicles = async () => {
    try {
      // Mock data met voertuigen door heel Nederland en België
      const mockVehicles: Vehicle[] = [
        {
          id: 1,
          licensePlate: 'XY-123-Z',
          brand: 'Volkswagen',
          model: 'Transporter',
          year: 2022,
          status: 'Active'
        },
        {
          id: 2,
          licensePlate: 'AB-456-C',
          brand: 'Mercedes-Benz',
          model: 'Sprinter',
          year: 2023,
          status: 'Active'
        },
        {
          id: 3,
          licensePlate: 'CD-789-E',
          brand: 'Ford',
          model: 'Transit',
          year: 2021,
          status: 'Maintenance'
        },
        {
          id: 4,
          licensePlate: 'FG-012-H',
          brand: 'Renault',
          model: 'Master',
          year: 2022,
          status: 'Active'
        },
        {
          id: 5,
          licensePlate: 'IJ-345-K',
          brand: 'Toyota',
          model: 'Proace',
          year: 2023,
          status: 'Active'
        },
        {
          id: 6,
          licensePlate: 'LM-678-N',
          brand: 'Fiat',
          model: 'Ducato',
          year: 2022,
          status: 'Active'
        },
        {
          id: 7,
          licensePlate: 'OP-901-Q',
          brand: 'Peugeot',
          model: 'Boxer',
          year: 2021,
          status: 'Maintenance'
        },
        {
          id: 8,
          licensePlate: '1-ABC-123', // Belgisch kenteken
          brand: 'Citroën',
          model: 'Jumper',
          year: 2023,
          status: 'Active'
        }
      ];
      setVehicles(mockVehicles);
    } catch (error) {
      console.error('Error fetching vehicles:', error);
      toast.error(t('vehicles.fetch.error') as string);
    }
  };

  const fetchVehicleLocations = async () => {
    try {
      // Simuleer bewegende voertuigen
      const now = Date.now();
      setVehicles(prevVehicles =>
        prevVehicles.map(vehicle => {
          // Basis coördinaten voor verschillende steden
          const baseLocation = {
            1: { lat: 52.3676, lng: 4.9041 }, // Amsterdam
            2: { lat: 51.4416, lng: 5.4697 }, // Eindhoven
            3: { lat: 52.2215, lng: 6.8937 }, // Enschede
            4: { lat: 51.0543, lng: 3.7174 }, // Gent
            5: { lat: 50.9307, lng: 5.3378 }, // Hasselt
            6: { lat: 51.9225, lng: 4.4792 }, // Rotterdam
            7: { lat: 52.0907, lng: 5.1214 }, // Utrecht
            8: { lat: 50.8503, lng: 4.3517 } // Brussel
          }[vehicle.id] || { lat: 52.3676, lng: 4.9041 };

          // Voeg kleine willekeurige beweging toe
          const movement = Math.sin(now / 1000) * 0.001;
          const randomSpeed = Math.floor(Math.random() * 80); // Snelheid tussen 0 en 80 km/h

          return {
            ...vehicle,
            location: {
              lat: baseLocation.lat + movement,
              lng: baseLocation.lng + movement,
              speed: randomSpeed
            }
          };
        })
      );
    } catch (error) {
      console.error('Error fetching vehicle locations:', error);
    }
  };

  return (
    <PageWrapper>
      <IconStyle /> {/* Add the styles to the DOM */}
      {/* Side Menu */}
      <SideMenu elevation={3}>
        <Typography variant="h6" sx={{ mb: 2 }}>
          {t('fleet.vehicles.title')}
        </Typography>
        <VehicleList>
          {vehicles.map(vehicle => (
            <VehicleListItem
              key={vehicle.id}
              onClick={() => {
                if (vehicle.location) {
                  setSelectedVehicle([vehicle.location.lat, vehicle.location.lng]);
                }
              }}>
              <ListItemText
                primary={vehicle.licensePlate}
                secondary={
                  <>
                    {vehicle.brand} {vehicle.model}
                    <br />
                    {vehicle.location ? `${vehicle.location.speed} km/h` : 'No data'}
                  </>
                }
              />
              <StatusChip label={vehicle.status} status={vehicle.status} size="small" />
            </VehicleListItem>
          ))}
        </VehicleList>
      </SideMenu>
      {/* Map Container */}
      <MapWrapper elevation={3}>
        <MapContainer
          center={[52.1326, 5.2913]} // Centreer op midden Nederland
          zoom={8} // Zoom uit om meer te zien
          style={{ height: '100%', width: '100%' }}>
          <MapController center={selectedVehicle || undefined} />
          <TileLayer
            url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
            attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
          />
          {vehicles.map(
            vehicle =>
              vehicle.location && (
                <Marker
                  key={vehicle.id}
                  position={[vehicle.location.lat, vehicle.location.lng]}
                  icon={createCarIcon(vehicle.status)}>
                  <Popup>
                    <Box sx={{ p: 1 }}>
                      <Typography variant="subtitle1" sx={{ fontWeight: 'bold' }}>
                        {vehicle.licensePlate}
                      </Typography>
                      <Typography variant="body2">
                        {vehicle.brand} {vehicle.model} ({vehicle.year})
                      </Typography>
                      <Typography variant="body2">Status: {vehicle.status}</Typography>
                      <Typography variant="body2">Speed: {vehicle.location.speed} km/h</Typography>
                    </Box>
                  </Popup>
                </Marker>
              )
          )}
        </MapContainer>
      </MapWrapper>
    </PageWrapper>
  );
};

export default FleetManagement;
